import { useEffect, useState } from "react";
import { getDatabase, ref, get, update } from "firebase/database";
import { ClipboardList } from "lucide-react";
import "./userQuotations.css";

const formatDate = (timestamp) => {
  if (!timestamp) return "-";

  const date = new Date(Number(timestamp));

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const tableHeaderStyle = {
  padding: "12px 14px",
  textAlign: "left",
  fontSize: 13,
  fontWeight: 600,
  color: "#222",
  background: "#e5e5e5",
  borderBottom: "1px solid #cfcfcf",
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  padding: "12px 14px",
  textAlign: "left",
  verticalAlign: "top",
  fontSize: 13,
  color: "#333",
  whiteSpace: "nowrap",
};

const statusButtonStyle = (status) => ({
  border: "none",
  borderRadius: 6,
  padding: "6px 10px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  background: status === "replied" ? "#dcfce7" : "#dbeafe",
  color: status === "replied" ? "#15803d" : "#2563eb",
});

const UserQuotations = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      const db = getDatabase();
      const quoteRef = ref(db, `quoteRequest/${quoteId}`);

      await update(quoteRef, {
        status: newStatus,
      });

      setQuotes((prevQuotes) =>
        prevQuotes.map((quote) =>
          quote.id === quoteId
            ? { ...quote, status: newStatus }
            : quote
        )
      );
    } catch (error) {
      console.error("Status update error:", error);
      alert("Failed to update status. Please try again.");
    }
  };

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const db = getDatabase();
        const quoteRef = ref(db, "quoteRequest");

        console.log("Fetching:", quoteRef.toString());

        const snapshot = await get(quoteRef);

        console.log("Exists:", snapshot.exists());
        console.log("Data:", snapshot.val());

        setLoading(false);

        if (!snapshot.exists()) {
          setQuotes([]);
          return;
        }

        const data = snapshot.val();
        const list = [];

        for (const id in data) {
          if (Object.prototype.hasOwnProperty.call(data, id)) {
            list.push({
              id,
              ...data[id],
            });
          }
        }

        setQuotes(list);
      } catch (error) {
        console.error("Quote fetch error:", error);
        setQuotes([]);
        setLoading(false);
      }
    };

    fetchQuotes();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          padding: 30,
          textAlign: "center",
          fontSize: 18,
        }}
      >
        Loading...
      </div>
    );
  }

  // Latest quotation requests first
  const sortedQuotes = [...quotes].sort(
    (a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)
  );

  return (
    <div style={{ padding: 25 }}>
      <h2
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <ClipboardList size={24} />
        Quotation Requests
      </h2>

      <p style={{ color: "#666", marginBottom: 20 }}>
        Total Requests: <b>{quotes.length}</b>
      </p>

      {quotes.length === 0 ? (
        <h3>No quotation requests found.</h3>
      ) : (
        <div
          style={{
            width: "100%",
            overflowX: "auto",
            background: "#fff",
            border: "1px solid #e5e5e5",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: 1200,
            }}
          >
            <thead>
              <tr>
                <th style={tableHeaderStyle}>Brand</th>
                <th style={tableHeaderStyle}>Status</th>
                <th style={tableHeaderStyle}>City</th>
                <th style={tableHeaderStyle}>Area</th>
                <th style={tableHeaderStyle}>Service Category</th>
                <th style={tableHeaderStyle}>Grade</th>
                <th style={tableHeaderStyle}>Quantity</th>
                <th style={tableHeaderStyle}>Unit</th>
                <th style={tableHeaderStyle}>Requirement</th>
                <th style={tableHeaderStyle}>Phone</th>
                <th style={tableHeaderStyle}>Email</th>
                <th style={tableHeaderStyle}>Created At</th>
              </tr>
            </thead>

            <tbody>
              {sortedQuotes.map((quote, index) => (
                <tr
                  key={quote.id}
                  style={{
                    borderTop: "1px solid #e5e5e5",
                    background:
                      index % 2 === 0 ? "#ffffff" : "#f5f5f5",
                  }}
                >
                  {/* Brand */}
                  <td
                    style={{
                      ...tableCellStyle,
                      background: "#eeeeee",
                    }}
                  >
                    {quote.brand || "-"}
                  </td>

                  {/* Status */}
                  <td
                    style={{
                      ...tableCellStyle,
                      background: "#f2f2f2",
                    }}
                  >
                    <select
                      value={(quote.status || "pending").toLowerCase()}
                      onChange={(e) =>
                        handleStatusChange(
                          quote.id,
                          e.target.value
                        )
                      }
                      style={statusButtonStyle(
                        (quote.status || "pending").toLowerCase()
                      )}
                      aria-label={`Change status for quotation ${quote.id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="replied">Replied</option>
                    </select>
                  </td>

                  {/* City */}
                  <td
                    style={{
                      ...tableCellStyle,
                      background: "#eeeeee",
                    }}
                  >
                    {quote.city || "-"}
                  </td>

                  {/* Area */}
                  <td
                    style={{
                      ...tableCellStyle,
                      background: "#f2f2f2",
                    }}
                  >
                    {quote.area || "-"}
                  </td>

                  {/* Service Category */}
                  <td style={tableCellStyle}>
                    {quote.serviceCategory || "-"}
                  </td>

                  {/* Grade */}
                  <td style={tableCellStyle}>
                    {quote.grade || "-"}
                  </td>

                  {/* Quantity */}
                  <td style={tableCellStyle}>
                    {quote.quantity || "-"}
                  </td>

                  {/* Unit */}
                  <td style={tableCellStyle}>
                    {quote.unit || "-"}
                  </td>

                  {/* Requirement */}
                  <td
                    style={{
                      ...tableCellStyle,
                      whiteSpace: "normal",
                      minWidth: 250,
                      maxWidth: 450,
                    }}
                  >
                    {quote.requirement || "-"}
                  </td>

                  {/* Phone */}
                  <td style={tableCellStyle}>
                    {quote.phone || "-"}
                  </td>

                  {/* Email */}
                  <td style={tableCellStyle}>
                    {quote.email || "-"}
                  </td>

                  {/* Created At */}
                  <td
                    style={{
                      ...tableCellStyle,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDate(quote.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserQuotations;